import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FaCheckCircle, FaTimesCircle, FaSpinner, FaHome, FaBook, FaSignInAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getUser, getUserRole } from '../utils/auth';
import { fetchWithToken } from '../services/api';

const PaymentComplete = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [paymentResult, setPaymentResult] = useState(null);
  const [error, setError] = useState(null);

  const isUserAuthenticated = () => {
    try {
      const user = getUser();
      return !!user;
    } catch {
      return false;
    }
  };

  const navigateToUserDashboard = () => {
    if (isUserAuthenticated()) {
      const userRole = getUserRole();
      if (userRole === 'ROLE_STUDENT') {
        navigate('/student-dashboard');
      } else if (userRole === 'ROLE_TEACHER') {
        navigate('/teacher-dashboard');
      } else {
        navigate('/');
      }
    } else {
      navigate('/login');
    }
  };

  useEffect(() => {
    const handlePaymentResult = async () => {
      try {
        const status = searchParams.get('status');
        const message = searchParams.get('message');
        const token = searchParams.get('token');
        const error = searchParams.get('error');

        console.log('Payment Complete - Parameters:', { status, message, token, error });

        // Eğer error parametresi varsa
        if (error === 'true') {
          const decodedMessage = message ? decodeURIComponent(message) : 'Ödeme işlemi başarısız oldu.';
          setPaymentResult({
            success: false,
            message: decodedMessage
          });
          toast.error(decodedMessage);
          setLoading(false);
          return;
        }

        // Başarılı ödeme durumu
        if (status === 'success') {
          setPaymentResult({
            success: true,
            message: 'Ödeme başarıyla tamamlandı!',
            paymentData: token ? { token } : null
          });
          toast.success('🎉 Ödeme başarıyla tamamlandı!');
          
          // Eğer token varsa ve kullanıcı login olduysa backend'e bildirim gönder
          if (token && isUserAuthenticated()) {
            try {
              await fetchWithToken(`/v1/payments/complete?token=${encodeURIComponent(token)}`, {
                method: 'POST'
              });
              console.log('Payment completion notification sent to backend');
            } catch (err) {
              console.log('Backend notification failed, but payment was successful:', err);
            }
          }
          
          setLoading(false);
          return;
        }

        // Başarısız ödeme durumu
        if (status === 'fail' || status === 'failure') {
          const decodedMessage = message ? decodeURIComponent(message) : 'Ödeme işlemi başarısız oldu.';
          setPaymentResult({
            success: false,
            message: decodedMessage
          });
          toast.error(decodedMessage);
          setLoading(false);
          return;
        }

        // Parametreler eksik veya belirsiz
        setError('Ödeme parametreleri eksik veya geçersiz.');
        setLoading(false);
        
      } catch (err) {
        console.error('Payment result processing error:', err);
        setError('Ödeme sonucu işlenirken bir hata oluştu.');
        setLoading(false);
      }
    };

    handlePaymentResult();
  }, [searchParams]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="mb-6">
            <FaSpinner className="h-16 w-16 text-blue-500 mx-auto animate-spin" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Ödeme Sonucu İşleniyor
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Ödeme sonucunuz kontrol ediliyor, lütfen bekleyin...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="mb-6">
            <FaTimesCircle className="h-16 w-16 text-red-500 mx-auto" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Bir Hata Oluştu
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {error}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={navigateToUserDashboard}
              className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
            >
              {isUserAuthenticated() ? <FaBook className="h-4 w-4" /> : <FaSignInAlt className="h-4 w-4" />}
              {isUserAuthenticated() ? 'Ders Taleplerim' : 'Giriş Yap'}
            </button>
            <button
              onClick={() => navigate('/')}
              className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
            >
              <FaHome className="h-4 w-4" />
              Ana Sayfa
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        <div className="mb-6">
          {paymentResult?.success ? (
            <FaCheckCircle className="h-16 w-16 text-green-500 mx-auto" />
          ) : (
            <FaTimesCircle className="h-16 w-16 text-red-500 mx-auto" />
          )}
        </div>
        
        <h2 className={`text-xl font-semibold mb-4 ${
          paymentResult?.success 
            ? 'text-green-600 dark:text-green-400' 
            : 'text-red-600 dark:text-red-400'
        }`}>
          {paymentResult?.success ? 'Ödeme Başarılı!' : 'Ödeme Başarısız!'}
        </h2>
        
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          {paymentResult?.message}
        </p>

        {/* Payment details for successful payments */}
        {paymentResult?.success && paymentResult?.paymentData && (
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6 text-left">
            <h3 className="font-medium text-gray-900 dark:text-white mb-2">Ödeme Detayları:</h3>
            <div className="space-y-1 text-sm">
              {paymentResult.paymentData.token && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">İşlem Token:</span>
                  <span className="font-medium text-gray-900 dark:text-white font-mono text-xs">
                    {paymentResult.paymentData.token.substring(0, 20)}...
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">İşlem Tarihi:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {new Date().toLocaleString('tr-TR')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Durum:</span>
                <span className="font-medium text-green-600 dark:text-green-400">
                  Başarılı
                </span>
              </div>
            </div>
          </div>
        )}
        
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={navigateToUserDashboard}
            className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
              paymentResult?.success
                ? 'bg-green-600 hover:bg-green-700 text-white'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {isUserAuthenticated() ? <FaBook className="h-4 w-4" /> : <FaSignInAlt className="h-4 w-4" />}
            {isUserAuthenticated() ? 'Ders Taleplerim' : 'Giriş Yap'}
          </button>
          <button
            onClick={() => navigate('/')}
            className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
          >
            <FaHome className="h-4 w-4" />
            Ana Sayfa
          </button>
        </div>
        {!paymentResult?.success && (
          <button
            onClick={navigateToUserDashboard}
            className="w-full mt-3 px-4 py-2 bg-orange-100 hover:bg-orange-200 dark:bg-orange-900/30 dark:hover:bg-orange-900/40 text-orange-700 dark:text-orange-300 rounded-lg font-medium transition-colors"
          >
            {isUserAuthenticated() ? 'Tekrar Dene' : 'Giriş Yapıp Tekrar Dene'}
          </button>
        )}
      </div>
    </div>
  );
};

export default PaymentComplete;